import {CYCLE_TIME} from './index';

// --------------------------------------------------------------------------------

export default class System {

    // generic system

    constructor(x_last = 0, y_last = 0) {
        this.x_last = x_last;
        this.y_last = y_last;
        this.parameters = {};
        if (this.getNextValues === undefined) {
            throw new TypeError('Method getNextValue must be overwritten');
        }
    }

    reset() {
        this.x_last = 0;
        this.y_last = 0;
    }

    updateParameters(parameters) {
        Object.keys(parameters).forEach(parameterKey => {
            if (this.parameters.hasOwnProperty(parameterKey)) {
                this.parameters[parameterKey] = parameters[parameterKey];
            }
        });
    }

    toString() {
        return this.constructor.name;
    }

}

// --------------------------------------------------------------------------------

export class PElement extends System {

    // proportional element
    // definition: G(s) = K_p

    constructor(parameters) {
        super();
        this.parameters.k_p = parameters.k_p !== undefined ? parameters.k_p : 10;
    }

    getNextValues(x) {
        this.y_last = this.parameters.k_p * x;
        return {y: this.y_last};
    }

    static getName(){
        return 'PElement';
    }

}

// --------------------------------------------------------------------------------

export class IElement extends System {

    // integral element
    // definition: G(s) = 1 / ( T_i * s)

    constructor(parameters) {
        super();
        this.parameters.t_i = parameters.t_i !== undefined ? parameters.t_i : 1;
        this.integral = 0;
    }

    getNextValues(x) {
        this.integral = this.integral + CYCLE_TIME * x;
        if (this.parameters.t_i === 0) {
            this.y_last = 0;
        } else {
            this.y_last = this.integral / this.parameters.t_i;
        }
        return {y: this.y_last, integral: this.integral};
    }

    reset() {
        super.reset();
        this.integral = 0;
    }

    static getName(){
        return 'IElement';
    }

}

// --------------------------------------------------------------------------------

export class DElement extends System {

    // differential element
    // definition: G(s) = T_d * s

    constructor(parameters) {
        super();
        this.parameters.t_d = parameters.t_d !== undefined ? parameters.t_d : 1;
        this.differential = 0;
    }

    getNextValues(x) {
        if(this.x_last != null){
            this.differential = ((x - this.x_last) / CYCLE_TIME);
        }else{
            this.differential = 0;
        }
        this.y_last = this.parameters.t_d * this.differential;
        this.x_last = x;
        return {y: this.y_last, differential: this.differential};
    }

    reset() {
        super.reset();
        this.differential = 0;
    }

    static getName(){
        return 'DElement';
    }

}

// --------------------------------------------------------------------------------

export class T1Element extends System {

    constructor(parameters, x_last=0) {
        super();
        this.parameters.t_1 = parameters.t_1 !== undefined ? parameters.t_1 : 1;
        this.x_last = x_last;
    }

    reset() {
        this.x_last = 0;
    }

    getNextValues(x) {
        // Euler
        // const alpha = CYCLE_TIME / (CYCLE_TIME + this.parameters.t_1);
        // this.y_last = (1 - alpha) * this.y_last + alpha * x;
        // Tustin
        const alpha = 2 * this.parameters.t_1 / CYCLE_TIME;
        this.y_last = 1 / (1 + alpha) * (-(1-alpha) * this.y_last + x + this.x_last);
        this.x_last = x;
        return {y: this.y_last};
    }

    static getName(){
        return 'T1Element';
    }

}

// --------------------------------------------------------------------------------

export class T2Element extends System {

    constructor(parameters, x_last1=0, x_last2=0, y_last2=0) {
        super();
        this.parameters.t_2 = parameters.t_2 !== undefined ? parameters.t_2 : 1;
        this.parameters.d = parameters.d !== undefined ? parameters.d : 0.2;
        this.x_last1 = x_last1;
        this.x_last2 = x_last2;
        this.y_last2 = y_last2;
    }

    reset() {
        this.x_last1 = 0;
        this.x_last2 = 0;
        this.y_last2 = 0;
    }

    getNextValues(x) {
        const alpha = 2 * this.parameters.t_2 / CYCLE_TIME;
        const a0 = alpha**2 + 2*this.parameters.d*alpha + 1;
        const a1 = -2*alpha**2 + 2;
        const a2 = alpha**2 - 2*this.parameters.d*alpha + 1;
        const y_last2_tmp = this.y_last;
        this.y_last = 1/a0 * (-a1 * this.y_last -a2 * this.y_last2 + x + 2 * this.x_last1 + this.x_last2);
        this.y_last2 = y_last2_tmp;
        this.x_last1 = x;
        this.x_last2 = this.x_last1;
        return {y: this.y_last};
    }

    static getName(){
        return 'T2Element';
    }

}

// --------------------------------------------------------------------------------

export class TtElement extends System {

    // dead-time element
    // definition: G(s) = exp( - T_t * s )

    constructor(parameters) {
        super();
        this.setDeadtimeAndReset(parameters.t_t !== undefined ? parameters.t_t : 1);
    }

    setDeadtimeAndReset(t_t) {
        this.parameters.t_t = t_t;
        this.bufferLength = Math.round(t_t / CYCLE_TIME) + 1;
        this.buffer = new Array(this.bufferLength).fill(0);
        this.index = 0;
    }

    updateParameters(parameters) {
        if (parameters.hasOwnProperty('t_t')) {
            this.setDeadtimeAndReset(parameters.t_t);
        }
    }

    getNextValues(x) {
        this.buffer[this.index] = x;
        this.index++;
        this.index = this.index % this.bufferLength;
        return {y: this.buffer[this.index]};
    }

    reset() {
        super.reset();
        this.setDeadtimeAndReset(this.parameters.t_t);
    }

    static getName(){
        return 'TtElement';
    }

}

// --------------------------------------------------------------------------------

export class PT1Element extends System {

    // proportial element with first-order low-pass filter
    // definition: G(s) = K_p / ( 1 + T_1 * s )

    constructor(parameters) {
        super();
        this.pelement = new PElement({k_p: parameters.k_p !== undefined ? parameters.k_p : 5});
        this.t1element = new T1Element({t_1: parameters.t_1 !== undefined ? parameters.t_1 : 3});
    }

    updateParameters(parameters) {
        this.pelement.updateParameters(parameters);
        this.t1element.updateParameters(parameters);
    }

    getNextValues(x) {
        this.y_last = this.pelement.getNextValues(this.t1element.getNextValues(x).y).y;
        return {y: this.y_last};
    }

    reset() {
        this.t1element.reset();
    }

    static getName(){
        return 'PT1Element';
    }

}

// --------------------------------------------------------------------------------

export class IT1Element extends System {

    // integal element with first-order low-pass filter
    // definition: G(s) = 1 / [ T_i * s * ( 1 + T_1 * s ) ]

    constructor(parameters) {
        super();
        this.ielement = new IElement({t_i: parameters.t_i !== undefined ? parameters.t_i : 5});
        this.t1element = new T1Element({t_1: parameters.t_1 !== undefined ? parameters.t_1 : 3});
    }

    updateParameters(parameters) {
        this.ielement.updateParameters(parameters);
        this.t1element.updateParameters(parameters);
    }

    getNextValues(x) {
        this.y_last = this.ielement.getNextValues(this.t1element.getNextValues(x).y).y;
        return {y: this.y_last};
    }

    reset() {
        this.ielement.reset();
        this.t1element.reset();
    }
    static getName(){
        return 'IT1Element';
    }

}

// --------------------------------------------------------------------------------

export class DT1Element extends System {

    // differential element with first-order low-pass filter
    // definition: G(s) = T_d * s / ( 1 + T_f * s)

    constructor(parameters) {
        super();
        this.delement = new DElement({t_d: parameters.t_d !== undefined ? parameters.t_d : 5});
        this.t1element = new T1Element({t_1: parameters.t_1 !== undefined ? parameters.t_1 : 3});
    }

    updateParameters(parameters) {
        this.delement.updateParameters(parameters);
        this.t1element.updateParameters(parameters);
    }

    getNextValues(x) {
        this.y_last = this.delement.getNextValues(this.t1element.getNextValues(x).y).y;
        return {y: this.y_last};
    }

    reset() {
        this.delement.reset();
        this.t1element.reset();
    }
    static getName(){
        return 'DT1Element';
    }

}

// --------------------------------------------------------------------------------

export class I2Element extends System {

    // double integrator
    // definition: G(s) = 1 / [ (T_i,1 * s) * (T_i,2 * s) ]

    constructor(parameters) {
        super();
        this.ielement1 = new IElement({t_i: parameters.t_i1 !== undefined ? parameters.t_i1 : 1});
        this.ielement2 = new IElement({t_i: parameters.t_i2 !== undefined ? parameters.t_i2 : 1});
    }

    updateParameters(parameters) {
        this.ielement1.updateParameters(parameters);
        this.ielement2.updateParameters(parameters);
    }

    getNextValues(x) {
        this.y_last = this.ielement2.getNextValues(this.ielement1.getNextValues(x).y).y;
        return {y: this.y_last};
    }

    reset() {
        this.ielement1.reset();
        this.ielement2.reset();
    }
    static getName(){
        return 'I2Element';
    }

}

// --------------------------------------------------------------------------------

export class PT1TtElement extends System {

    // first-order low-pass filter with dead time
    // definition: G(s) = K_p / ( 1 + T_1 * s ) * exp( -T_t * s )

    constructor(parameters) {
        super();
        this.pt1element = new PT1Element({k_p: parameters.k_p !== undefined ? parameters.k_p : 5, t_1: parameters.t_1 !== undefined ? parameters.t_1 : 3});
        this.ttelement = new TtElement({t_t: parameters.t_t !== undefined ? parameters.t_t : 1});
    }

    updateParameters(parameters) {
        this.pt1element.updateParameters(parameters);
        this.ttelement.updateParameters(parameters);
    }

    getNextValues(x) {
        this.y_last = this.pt1element.getNextValues(this.ttelement.getNextValues(x).y).y;
        return {y: this.y_last};
    }

    reset() {
        this.pt1element.reset();
        this.ttelement.reset();
    }

    static getName(){
        return 'PT1TtElement';
    }

}

// --------------------------------------------------------------------------------

export class Hysteresis extends System {
    
    // hysteresis
    
    constructor(parameters) {
        super();
        this.parameters.e_upper = parameters.e_upper !== undefined ? parameters.e_upper : 3;
        this.parameters.e_lower = parameters.e_lower !== undefined ? parameters.e_lower : -3;
        this.parameters.u_upper = parameters.u_upper !== undefined ? parameters.u_upper : 15;
        this.parameters.u_lower = parameters.u_lower !== undefined ? parameters.u_lower : -15;
        this.reset();
    }

    getNextValues(x) {
        let y;
        if (x >= this.parameters.e_upper) {
            y = this.parameters.u_upper;
            this.last_on = true;
        } else if (x <= this.parameters.e_lower) {
            y = this.parameters.u_lower;
            this.last_on = false;
        } else {
            y = this.last_on ? this.parameters.u_upper : this.parameters.u_lower;
        }
        return {y: y};
    }

    reset() {
        super.reset();
        this.last_on = false;
    }

    static getName(){
        return 'Hysteresis';
    }
}

// --------------------------------------------------------------------------------
